import React, { useState } from 'react';
import AnimatedSection from '../components/AnimatedSection';
import { team } from '../data/content';

const Team = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const openModal = (member) => {
    setSelectedMember(member);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMember(null);
  };

  // Function to generate WhatsApp link for team member with their personal number
  const getWhatsAppLink = (member) => {
    const message = `Hi! I'm interested in connecting with ${member.name} from VTECH CODERS. Could you please help me with more information?`;
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${member.whatsappNumber}?text=${encodedMessage}`;
  };

  return (
    <>
      <section className="team-section">
        {/* Animated Background */}
        <div className="animated-background">
          <div className="gradient-sphere-1"></div>
          <div className="gradient-sphere-2"></div>
          <div className="gradient-sphere-3"></div>
          <div className="particle-container">
            {[...Array(30)].map((_, i) => (
              <div key={i} className="particle" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${5 + Math.random() * 10}s`,
                width: `${2 + Math.random() * 6}px`,
                height: `${2 + Math.random() * 6}px`
              }}></div>
            ))}
          </div>
        </div>

        <div className="container">
          <AnimatedSection animation="fade-up">
            <div className="section-header">
              <span className="section-subtitle">Our Leadership Team</span>
              <h2 className="section-title">Meet Our Leaders</h2>
              <p className="section-description">
                Passionate experts dedicated to transforming your business with innovative solutions
              </p>
            </div>
          </AnimatedSection>
          
          <div className="team-grid">
            {team.map((member, idx) => (
              <AnimatedSection key={idx} animation="scale-up" delay={idx * 100}>
                <div className="team-card" onClick={() => openModal(member)}>
                  <div className="card-inner">
                    {/* Image Container with Overlay */}
                    <div className="image-container">
                      <div className="image-glow"></div>
                      <img 
                        src={member.img} 
                        alt={member.name} 
                        className="team-image"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/300?text=Team+Member'}
                      />
                      <div className="image-overlay">
                        <div className="overlay-content">
                          <i className="fas fa-user-circle"></i>
                          <span>View Profile</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* WhatsApp Floating Icon */}
                    <div className="whatsapp-floating">
                      <a 
                        href={getWhatsAppLink(member)} 
                        className="whatsapp-icon" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fab fa-whatsapp"></i>
                      </a>
                    </div>
                    
                    {/* Member Info */}
                    <div className="member-info">
                      <h3>{member.name}</h3>
                      <p className="member-role">{member.role}</p>
                      <p className="member-bio">{member.bio}</p>
                      
                      {/* Expertise Tags */}
                      {member.expertise && (
                        <div className="expertise-tags">
                          {member.expertise.map((skill, i) => (
                            <span key={i} className="tag">{skill}</span>
                          ))}
                        </div>
                      )}
                      
                      {/* Connect on WhatsApp Button */}
                      <a 
                        href={getWhatsAppLink(member)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="connect-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fab fa-whatsapp"></i>
                        <span>Connect on WhatsApp</span>
                        <i className="fas fa-arrow-right"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Member Profile Modal */}
      {showModal && selectedMember && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            
            <div className="modal-content">
              <div className="modal-image-section">
                <div className="modal-image-wrapper">
                  <img 
                    src={selectedMember.img} 
                    alt={selectedMember.name}
                    onError={(e) => e.target.src = 'https://via.placeholder.com/400?text=Team+Member'}
                  />
                  <div className="modal-image-overlay"></div>
                </div>
              </div>
              
              <div className="modal-info-section">
                <h2>{selectedMember.name}</h2>
                <p className="modal-role">{selectedMember.role}</p>
                <p className="modal-bio">{selectedMember.bio}</p>
                
                {selectedMember.expertise && (
                  <div className="modal-expertise">
                    <h4>Core Expertise</h4>
                    <div className="expertise-grid">
                      {selectedMember.expertise.map((skill, i) => (
                        <span key={i} className="expertise-item">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedMember.achievements && (
                  <div className="modal-achievements">
                    <h4>Key Achievements</h4>
                    <ul>
                      {selectedMember.achievements.map((achievement, i) => (
                        <li key={i}>
                          <i className="fas fa-trophy"></i>
                          <span>{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="modal-contact-info">
                  <div className="contact-detail">
                    <i className="fas fa-phone-alt"></i>
                    <span>WhatsApp: +{selectedMember.whatsappNumber}</span>
                  </div>
                  {selectedMember.email && (
                    <div className="contact-detail">
                      <i className="fas fa-envelope"></i>
                      <span>{selectedMember.email}</span>
                    </div>
                  )}
                </div>
                
                <div className="modal-whatsapp">
                  <a 
                    href={getWhatsAppLink(selectedMember)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-connect-btn"
                  >
                    <i className="fab fa-whatsapp"></i>
                    <span>Connect on WhatsApp</span>
                    <i className="fas fa-chevron-right"></i>
                  </a>
                  <p className="whatsapp-note">
                    <i className="fas fa-clock"></i>
                    Response within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Main Section */
        .team-section {
          padding: 5rem 0;
          position: relative;
          overflow: hidden;
          min-height: 100vh;
        }
        
        /* Animated Background */
        .animated-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
        }
        
        .gradient-sphere-1,
        .gradient-sphere-2,
        .gradient-sphere-3 {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          animation: floatSphere 20s ease-in-out infinite;
        }
        
        .gradient-sphere-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.3), rgba(139, 92, 246, 0.2));
          top: -250px;
          left: -250px;
          animation-delay: 0s;
        }
        
        .gradient-sphere-2 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.25), rgba(236, 72, 153, 0.15));
          bottom: -300px;
          right: -300px;
          animation-delay: -5s;
        }
        
        .gradient-sphere-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(6, 182, 212, 0.2), rgba(59, 130, 246, 0.15));
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }
        
        @keyframes floatSphere {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(50px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-50px, 50px) scale(0.9);
          }
        }
        
        /* Particles */
        .particle-container {
          position: absolute;
          width: 100%;
          height: 100%;
        }
        
        .particle {
          position: absolute;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 50%;
          opacity: 0.3;
          animation: floatParticle linear infinite;
        }
        
        @keyframes floatParticle {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.5;
          }
          90% {
            opacity: 0.5;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 4rem;
          position: relative;
          z-index: 2;
        }
        
        .section-subtitle {
          display: inline-block;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 1rem;
        }
        
        .section-title {
          font-size: 2.8rem;
          font-weight: 800;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #1e293b, #3b82f6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        .section-description {
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
          font-size: 1.1rem;
        }
        
        /* Team Grid */
        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
          position: relative;
          z-index: 2;
        }
        
        /* Team Card */
        .team-card {
          cursor: pointer;
          position: relative;
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .card-inner {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          transition: all 0.4s ease;
          border: 1px solid rgba(59, 130, 246, 0.1);
          position: relative;
        }
        
        .team-card:hover .card-inner {
          transform: translateY(-15px);
          box-shadow: 0 25px 40px rgba(0, 0, 0, 0.15);
          border-color: rgba(59, 130, 246, 0.3);
        }
        
        /* Image Container */
        .image-container {
          position: relative;
          overflow: hidden;
          aspect-ratio: 1 / 1;
        }
        
        .image-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.3), transparent);
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 1;
        }
        
        .team-card:hover .image-glow {
          opacity: 1;
        }
        
        .team-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .team-card:hover .team-image {
          transform: scale(1.1);
        }
        
        .image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(139, 92, 246, 0.8));
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.4s ease;
          cursor: pointer;
        }
        
        .team-card:hover .image-overlay {
          opacity: 1;
        }
        
        .overlay-content {
          text-align: center;
          color: white;
          transform: translateY(20px);
          transition: transform 0.4s ease;
        }
        
        .team-card:hover .overlay-content {
          transform: translateY(0);
        }
        
        .overlay-content i {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          display: block;
        }
        
        .overlay-content span {
          font-weight: 600;
        }
        
        /* WhatsApp Floating Icon */
        .whatsapp-floating {
          position: absolute;
          bottom: 20px;
          right: 20px;
          z-index: 2;
          transform: translateX(20px);
          opacity: 0;
          transition: all 0.4s ease;
        }
        
        .team-card:hover .whatsapp-floating {
          transform: translateX(0);
          opacity: 1;
        }
        
        .whatsapp-icon {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #25D366, #128C7E);
          color: white;
          transition: all 0.3s ease;
          text-decoration: none;
          box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);
          font-size: 1.4rem;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .whatsapp-icon:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(37, 211, 102, 0.5);
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        
        /* Member Info */
        .member-info {
          padding: 1.5rem;
          text-align: center;
        }
        
        .member-info h3 {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }
        
        .member-role {
          color: #3b82f6;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
        }
        
        .member-bio {
          color: #64748b;
          font-size: 0.85rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }
        
        /* Expertise Tags */
        .expertise-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
          margin-bottom: 1rem;
        }
        
        .tag {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          padding: 4px 10px;
          border-radius: 50px;
          font-size: 0.7rem;
          font-weight: 500;
          color: #3b82f6;
        }
        
        /* Connect Button */
        .connect-btn {
          background: linear-gradient(135deg, #25D366, #128C7E);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s ease;
          text-decoration: none;
          width: 100%;
        }
        
        .connect-btn:hover {
          transform: translateY(-2px);
          gap: 15px;
          box-shadow: 0 5px 20px rgba(37, 211, 102, 0.4);
        }
        
        .connect-btn i:first-child {
          font-size: 1.1rem;
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3000;
          animation: fadeIn 0.3s ease;
        }
        
        .modal-container {
          position: relative;
          max-width: 900px;
          width: 90%;
          max-height: 85vh;
          background: white;
          border-radius: 32px;
          overflow: hidden;
          animation: slideUp 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          border: none;
          color: white;
          font-size: 1.2rem;
          cursor: pointer;
          z-index: 10;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modal-close:hover {
          background: #ef4444;
          transform: rotate(90deg);
        }
        
        .modal-content {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 2rem;
          padding: 2rem;
        }
        
        .modal-image-section {
          position: relative;
        }
        
        .modal-image-wrapper {
          border-radius: 20px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 1 / 1;
        }
        
        .modal-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .modal-image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
        }
        
        .modal-info-section {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .modal-info-section h2 {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin: 0;
        }
        
        .modal-role {
          color: #3b82f6;
          font-weight: 600;
          font-size: 1rem;
        }
        
        .modal-bio {
          color: #64748b;
          line-height: 1.6;
        }
        
        .modal-expertise h4,
        .modal-achievements h4 {
          font-size: 1rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.75rem;
        }
        
        .expertise-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .expertise-item {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          padding: 6px 12px;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #3b82f6;
        }
        
        .modal-achievements ul {
          list-style: none;
          padding: 0;
        }
        
        .modal-achievements li {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #475569;
        }
        
        .modal-achievements li i {
          color: #f59e0b;
        }
        
        .modal-contact-info {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          border-radius: 16px;
          padding: 1rem;
          margin: 0.5rem 0;
        }
        
        .contact-detail {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #1e293b;
        }
        
        .contact-detail:last-child {
          margin-bottom: 0;
        }
        
        .contact-detail i {
          width: 24px;
          color: #25D366;
          font-size: 1rem;
        }
        
        .modal-whatsapp {
          margin-top: 0.5rem;
        }
        
        .whatsapp-connect-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: linear-gradient(135deg, #25D366, #128C7E);
          color: white;
          padding: 12px 20px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .whatsapp-connect-btn:hover {
          transform: translateY(-2px);
          gap: 15px;
          box-shadow: 0 5px 20px rgba(37, 211, 102, 0.4);
        }
        
        .whatsapp-connect-btn i:first-child {
          font-size: 1.3rem;
        }
        
        .whatsapp-note {
          text-align: center;
          margin-top: 0.8rem;
          font-size: 0.75rem;
          color: #64748b;
        }
        
        .whatsapp-note i {
          margin-right: 4px;
        }
        
        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        /* Responsive Styles */
        @media (max-width: 768px) {
          .section-title {
            font-size: 2rem;
          }
          
          .team-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .modal-content {
            grid-template-columns: 1fr;
            padding: 1.5rem;
          }
          
          .modal-container {
            width: 95%;
            max-height: 90vh;
            overflow-y: auto;
          }
          
          .whatsapp-icon {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
          }
        }
        
        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .card-inner {
            background: rgba(30, 41, 59, 0.9);
          }
          
          .member-info h3 {
            color: #f1f5f9;
          }
          
          .member-bio {
            color: #94a3b8;
          }
          
          .modal-container {
            background: #1e293b;
          }
          
          .modal-info-section h2 {
            color: #f1f5f9;
          }
          
          .modal-bio {
            color: #94a3b8;
          }
          
          .modal-expertise h4,
          .modal-achievements h4 {
            color: #f1f5f9;
          }
          
          .modal-achievements li {
            color: #cbd5e1;
          }
          
          .contact-detail {
            color: #f1f5f9;
          }
        }
      `}</style>
    </>
  );
};

export default Team;